// MaskProcessor.js
import { upscaleImageData } from './imageProcessing';
import logger from './logger';

export class MaskProcessor {
  constructor(targetWidth, targetHeight, options = {}) {
    this.targetWidth = targetWidth;
    this.targetHeight = targetHeight;
    this.options = Object.assign(
      {
        thresholdLow: 0.25,
        thresholdHigh: 0.65,
        morphologicalRadius: {
          erosion: 3,
          dilation: 2,
        },
        featherRadius: 2,
        emaAlpha: 0.75,
        disableEMA: false,
      },
      options
    );
    // Use WebGL if available; otherwise, fallback to CPU.
    this.useWebGL = MaskProcessor.isWebGLAvailable();
    // Internal EMA storage.
    this.emaMask = null;
  }

  // Static helper: Check whether WebGL is available.
  static isWebGLAvailable() {
    const canvas = document.createElement('canvas');
    return !!(
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    );
  }

  // When dimensions change, update them and reset EMA.
  updateDimensions(newWidth, newHeight) {
    this.targetWidth = newWidth;
    this.targetHeight = newHeight;
    // Reset EMA because dimensions have changed.
    this.emaMask = null;
  }

  // Update the EMA with a new processed mask.
  updateEMA(newMask) {
    // If no EMA exists, initialize it.
    if (!this.emaMask) {
      this.emaMask = newMask;
      return this.emaMask;
    }

    // If EMA is disabled, simply replace the EMA with the new mask.
    if (this.options.disableEMA) {
      this.emaMask = newMask;
      return this.emaMask;
    }

    const { emaAlpha, thresholdLow, thresholdHigh } = this.options;
    const width = newMask.width;
    const height = newMask.height;
    const result = new Uint8ClampedArray(newMask.data.length);

    // For each pixel, decide how to update.
    for (let i = 0; i < newMask.data.length; i += 4) {
      const newAlphaNorm = newMask.data[i + 3] / 255;
      const oldAlphaNorm = this.emaMask.data[i + 3] / 255;

      if (newAlphaNorm < thresholdLow) {
        // New mask says background (low confidence), so force background.
        result[i] = 0;
        result[i + 1] = 0;
        result[i + 2] = 0;
        result[i + 3] = 0;
      } else if (newAlphaNorm > thresholdHigh && oldAlphaNorm > thresholdHigh) {
        // Both new and old masks are highly confident.
        // Blend them using EMA.
        result[i] =
          emaAlpha * newMask.data[i] + (1 - emaAlpha) * this.emaMask.data[i];
        result[i + 1] =
          emaAlpha * newMask.data[i + 1] +
          (1 - emaAlpha) * this.emaMask.data[i + 1];
        result[i + 2] =
          emaAlpha * newMask.data[i + 2] +
          (1 - emaAlpha) * this.emaMask.data[i + 2];
        result[i + 3] =
          emaAlpha * newMask.data[i + 3] +
          (1 - emaAlpha) * this.emaMask.data[i + 3];
      } else {
        // In all other cases, let the new mask dominate.
        result[i] = newMask.data[i];
        result[i + 1] = newMask.data[i + 1];
        result[i + 2] = newMask.data[i + 2];
        result[i + 3] = newMask.data[i + 3];
      }
    }
    this.emaMask = new ImageData(result, width, height);
    return this.emaMask;
  }

  // Public method to process a segmentation mask.
  process(maskData) {
    // Upscale if necessary.
    if (
      maskData.width !== this.targetWidth ||
      maskData.height !== this.targetHeight
    ) {
      maskData = upscaleImageData(
        maskData,
        this.targetWidth,
        this.targetHeight
      );
    }
    let processed;
    if (this.useWebGL) {
      processed = this._processMaskWithWebGL(
        maskData,
        this.targetWidth,
        this.targetHeight,
        this.options
      );
    } else {
      processed = this._processMaskCPU(maskData, this.options);
    }
    // Update the internal EMA with the new processed mask.
    return this.updateEMA(processed);
  }

  // CPU fallback version.
  _processMaskCPU(maskData, options = {}) {
    const { thresholdLow = 0.55, thresholdHigh = 0.65 } = options;
    const width = maskData.width;
    const height = maskData.height;
    const data = new Uint8ClampedArray(maskData.data);

    // Soft thresholding.
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3] / 255;
      let newAlpha = 0;
      if (alpha < thresholdLow) {
        newAlpha = 0;
      } else if (alpha > thresholdHigh) {
        newAlpha = 1;
      } else {
        newAlpha = (alpha - thresholdLow) / (thresholdHigh - thresholdLow);
      }
      data[i + 3] = newAlpha * 255;
    }

    return new ImageData(data, width, height);
  }

  // GPU-based processing using WebGL shaders.
  _processMaskWithWebGL(maskData, targetWidth, targetHeight, options = {}) {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      throw new Error('WebGL not supported');
    }

    // Vertex shader source
    const vertexShaderSource = `
    attribute vec2 a_position;
    attribute vec2 a_texCoord;
    varying vec2 v_texCoord;
    void main() {
      gl_Position = vec4(a_position, 0.0, 1.0);
      v_texCoord = a_texCoord;
    }
  `;

    // Fragment shader including erosion, dilation, thresholding, and feathering.
    const fragmentShaderSource = `
    precision mediump float;
    varying vec2 v_texCoord;
    uniform sampler2D u_mask;
    uniform float u_thresholdLow;
    uniform float u_thresholdHigh;
    uniform vec2 u_texelSize;
    // Morphology parameters (in texels)
    uniform float u_morphErosionRadius;
    uniform float u_morphDilationRadius;
    // Feathering parameter (in texels)
    uniform float u_featherRadius;

    void main() {
      // Start by sampling the original mask probability.
      float alpha = texture2D(u_mask, v_texCoord).a;

      // --- Erosion: take the minimum value over a neighborhood.
      if (u_morphErosionRadius > 0.0) {
        float minAlpha = alpha;
        int rad = int(u_morphErosionRadius);
        // Use a fixed loop bound for safety (ensure your radius is <= 10)
        for (int dy = -10; dy <= 10; dy++) {
          if(dy > rad || dy < -rad) continue;
          for (int dx = -10; dx <= 10; dx++) {
            if(dx > rad || dx < -rad) continue;
            vec2 offset = vec2(float(dx), float(dy)) * u_texelSize;
            float sampleAlpha = texture2D(u_mask, v_texCoord + offset).a;
            minAlpha = min(minAlpha, sampleAlpha);
          }
        }
        alpha = minAlpha;
      }

      // --- Dilation: take the maximum value over a neighborhood.
      if (u_morphDilationRadius > 0.0) {
        float maxAlpha = alpha;
        int rad = int(u_morphDilationRadius);
        for (int dy = -10; dy <= 10; dy++) {
          if(dy > rad || dy < -rad) continue;
          for (int dx = -10; dx <= 10; dx++) {
            if(dx > rad || dx < -rad) continue;
            vec2 offset = vec2(float(dx), float(dy)) * u_texelSize;
            float sampleAlpha = texture2D(u_mask, v_texCoord + offset).a;
            maxAlpha = max(maxAlpha, sampleAlpha);
          }
        }
        alpha = maxAlpha;
      }

      // --- Soft thresholding: convert the (morphed) probability into a soft binary value.
      float processedAlpha;
      if (alpha < u_thresholdLow) {
        processedAlpha = 0.0;
      } else if (alpha > u_thresholdHigh) {
        processedAlpha = 1.0;
      } else {
        processedAlpha = (alpha - u_thresholdLow) / (u_thresholdHigh - u_thresholdLow);
      }

      // --- Feathering: use the thresholded value from neighboring pixels.
      float finalAlpha = processedAlpha;
      if (u_featherRadius > 0.0) {
        int rad = int(u_featherRadius);
        float sum = 0.0;
        float count = 0.0;
        // Loop over the neighborhood defined by featherRadius.
        for (int dy = -10; dy <= 10; dy++) {
          if(dy > rad || dy < -rad) continue;
          for (int dx = -10; dx <= 10; dx++) {
            if(dx > rad || dx < -rad) continue;
            vec2 offset = vec2(float(dx), float(dy)) * u_texelSize;
            // Sample the neighbor's original alpha.
            float neighborAlpha = texture2D(u_mask, v_texCoord + offset).a;
            // Apply the same thresholding logic to compute the neighbor's processed value.
            float neighborProcessed;
            if (neighborAlpha < u_thresholdLow) {
              neighborProcessed = 0.0;
            } else if (neighborAlpha > u_thresholdHigh) {
              neighborProcessed = 1.0;
            } else {
              neighborProcessed = (neighborAlpha - u_thresholdLow) / (u_thresholdHigh - u_thresholdLow);
            }
            sum += neighborProcessed;
            count += 1.0;
          }
        }
        float blurred = sum / count; // Average the thresholded values.
        // Blend the current processed value with the blurred (averaged) neighborhood value.
        finalAlpha = mix(processedAlpha, blurred, 0.5);
      }

      gl_FragColor = vec4(finalAlpha, finalAlpha, finalAlpha, finalAlpha);
    }
  `;

    // Helper to compile a shader.
    function compileShader(gl, source, type) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        logger.error('Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vertexShader = compileShader(
      gl,
      vertexShaderSource,
      gl.VERTEX_SHADER
    );
    const fragmentShader = compileShader(
      gl,
      fragmentShaderSource,
      gl.FRAGMENT_SHADER
    );
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      logger.error('Program link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    gl.useProgram(program);

    // Set up a full-screen quad.
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const positions = new Float32Array([
      -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    const a_position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(a_position);
    gl.vertexAttribPointer(a_position, 2, gl.FLOAT, false, 0, 0);

    // Set up texture coordinates.
    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    const texCoords = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
    const a_texCoord = gl.getAttribLocation(program, 'a_texCoord');
    gl.enableVertexAttribArray(a_texCoord);
    gl.vertexAttribPointer(a_texCoord, 2, gl.FLOAT, false, 0, 0);

    // Upload the mask texture.
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      maskData.width,
      maskData.height,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      maskData.data
    );

    // Set uniforms.
    const u_thresholdLow = gl.getUniformLocation(program, 'u_thresholdLow');
    const u_thresholdHigh = gl.getUniformLocation(program, 'u_thresholdHigh');
    const u_texelSize = gl.getUniformLocation(program, 'u_texelSize');
    const u_morphErosionRadius = gl.getUniformLocation(
      program,
      'u_morphErosionRadius'
    );
    const u_morphDilationRadius = gl.getUniformLocation(
      program,
      'u_morphDilationRadius'
    );
    const u_featherRadius = gl.getUniformLocation(program, 'u_featherRadius');

    gl.uniform1f(u_thresholdLow, options.thresholdLow || 0.55);
    gl.uniform1f(u_thresholdHigh, options.thresholdHigh || 0.65);
    gl.uniform2f(u_texelSize, 1.0 / maskData.width, 1.0 / maskData.height);

    const morph = options.morphologicalRadius || {};
    gl.uniform1f(u_morphErosionRadius, morph.erosion || 0.0);
    gl.uniform1f(u_morphDilationRadius, morph.dilation || 0.0);
    gl.uniform1f(u_featherRadius, options.featherRadius || 0.0);

    // Render.
    gl.viewport(0, 0, targetWidth, targetHeight);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Read the pixels back from the framebuffer.
    const pixels = new Uint8Array(targetWidth * targetHeight * 4);
    gl.readPixels(
      0,
      0,
      targetWidth,
      targetHeight,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels
    );

    return new ImageData(
      new Uint8ClampedArray(pixels),
      targetWidth,
      targetHeight
    );
  }
}
